import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardTitle,
    CardBody,
    Button,
    UncontrolledTooltip,
    Spinner,
} from 'reactstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import footballService from '../../services/api/football-service.js';

// Add this helper function inside your MatchDetails component
const truncateId = (id) => {
    if (!id || id.length < 20) return id;
    return `${id.slice(0, 10)}...${id.slice(-10)}`;
};
const TeamLogo = ({ team }) => {
    const [logoFailed, setLogoFailed] = useState(false);
    useEffect(() => { if (team) { setLogoFailed(false); } }, [team]);
    const logoUrl = `/logos/${team?.slug}.png`;
    if (!team) { return <div className="d-flex align-items-center justify-content-center bg-light border rounded-circle mx-auto" style={{ width: '6rem', height: '6rem' }}>?</div>; }
    if (logoFailed) { return <div className="d-flex align-items-center justify-content-center bg-light border rounded-circle mx-auto" style={{ width: '6rem', height: '6rem' }}><span className="h4 font-weight-bold text-dark">{team.code}</span></div>; }
    return <img src={logoUrl} alt={team.name} className="mx-auto rounded-circle" style={{ width: '6rem', height: '6rem', objectFit: 'contain' }} onError={() => setLogoFailed(true)} />;
};

const EventIcon = ({ event }) => {
    switch (event) {
        case 'goal': return <span className="text-success text-xl" title="Goal">⚽</span>;
        case 'card': return <span className="text-warning text-xl" title="Card">■</span>;
        case 'substitution': return <span className="text-info text-xl" title="Substitution">🔄</span>;
        default: return <span className="text-muted text-xl">•</span>;
    }
};

const StatBar = ({ label, homeValue, awayValue }) => {
    const total = (homeValue || 0) + (awayValue || 0);
    const homePercent = total > 0 ? ((homeValue || 0) / total) * 100 : 50;
    return (
        <div className="my-3">
            <div className="d-flex justify-content-between align-items-center text-sm font-weight-bold mb-1">
                <span className="text-dark">{homeValue ?? 'N/A'}</span>
                <span className="text-muted text-uppercase tracking-wider text-xs">{label}</span>
                <span className="text-dark">{awayValue ?? 'N/A'}</span>
            </div>
            <div className="progress" style={{ height: '0.5rem' }}>
                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${homePercent}%` }}></div>
                <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${100 - homePercent}%` }}></div>
            </div>
        </div>
    );
};

const renderPlayerList = (team, matchData, getPositionEmoji) => {
    if (!team || !matchData?.players) return <p className="text-sm text-muted">Not available.</p>;
    const getPlayer = (playerKey) => matchData.players.find(p => p.key === playerKey);
    const starters = (team.lineup || []).map(getPlayer).filter(Boolean);
    const bench = (team.bench || []).map(getPlayer).filter(Boolean);
    return (
        <>
            <h5 className="h5 font-weight-bold text-dark mt-4 mb-2">Starting XI</h5>
            {starters.length > 0 ? (
                <ul className="list-unstyled space-y-1 text-sm text-dark">
                    {starters.map(p => (<li key={p.key}><span className="text-muted me-2">#{p.jersey_number}</span><span className="text-muted me-2">{getPositionEmoji(p.match_role)}</span>{p.name} <span className="text-muted text-xs">({p.match_role})</span></li>))}
                </ul>
            ) : <p className="text-sm text-muted">Not available yet.</p>}
            <h5 className="h5 font-weight-bold text-dark mt-4 mb-2">Substitutes</h5>
            {bench.length > 0 ? (
                <ul className="list-unstyled space-y-1 text-sm text-dark">
                    {bench.map(p => (<li key={p.key}><span className="me-2">#{p.jersey_number}</span><span className="me-2">{getPositionEmoji(p.match_role)}</span>{p.name} <span className="text-muted text-xs">({p.match_role})</span></li>))}
                </ul>
            ) : <p className="text-sm text-muted">No substitutes listed.</p>}
        </>
    );
};


// --- MAIN COMPONENT ---
function MatchDetails() {
    const [matchData, setMatchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedItem, setCopiedItem] = useState(null);
    const { mslug } = useParams();
    const [showDetails, setShowDetails] = useState(false);

    const fetchMatchDetails = useCallback(async () => {
        setLoading(true);
        setError(null);
        setShowDetails(false);
        try {
            const result = await footballService.getMatchDetails(mslug);
            setMatchData(result.data);
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.message || 'Match not found or API error';
            setError(errorMessage);
        } finally {
            setLoading(false);
            setTimeout(() => setShowDetails(true), 50);
        }
    }, [mslug]);

    useEffect(() => {
        fetchMatchDetails();
    }, [fetchMatchDetails]);

    const handleCopy = (text, itemName) => {
        if (!text) return;
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedItem(itemName);
                setTimeout(() => setCopiedItem(null), 2000);
            })
            .catch(err => {
                console.error(`Failed to copy ${itemName}: `, err);
            });
    };

    const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-IN", {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: "Asia/Kolkata" // Forces IST
    });
  };
   const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp * 1000).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,           // Converts 24h -> 12h (AM/PM)
      timeZone: "Asia/Kolkata" // Converts UTC -> IST
    });
  };

    const getTeam = (teamId) => matchData?.teams?.find(t => t.key === teamId);
    const getPlayerName = (playerId) => matchData?.players?.find(p => p.key === playerId)?.name || 'N/A';
    const getPositionEmoji = (role) => {
        switch (role) {
            case 'goalkeeper': return '🧤';
            case 'defender': return '🛡️';
            case 'midfielder': return '⚙️';
            case 'striker': return '⚡';
            default: return '⚽';
        }
    };
    const getEventStyle = (event) => {
        switch (event) {
            case 'goal': return 'bg-success text-white border-start border-success';
            case 'card': return 'bg-warning text-white border-start border-warning';
            case 'substitution': return 'bg-info text-white border-start border-info';
            default: return 'bg-light border-start border-muted text-dark';
        }
    };

    if (loading) {
        return (<div className="content text-center py-5"> <Spinner color="primary" /> <p className="mt-2">Loading match details...</p> </div>);
    }
    if (error) {
        return (<div className="content text-center py-5"> <Card className="text-danger p-4"> <CardTitle tag="h4" className="text-danger">Error: {error}</CardTitle> <p> <Link to="/dashboard/football/data-center?tab=matches" className="btn btn-primary mt-3">&larr; Back to Match List</Link> </p> </Card> </div>);
    }
    if (!matchData) return null;

    const homeTeam = getTeam(matchData.match.home);
    const awayTeam = getTeam(matchData.match.away);
    const winner = getTeam(matchData.match_result?.winner);
    const manOfTheMatch = getPlayerName(matchData.match_result?.man_of_the_match?.[0]);
    const homeSubs = matchData.timeline?.filter(e => e.event === 'substitution' && e.data.team === homeTeam?.slug).length || 0;
    const awaySubs = matchData.timeline?.filter(e => e.event === 'substitution' && e.data.team === awayTeam?.slug).length || 0;

    const isMatchLive = matchData.match.status === 'live';
    const isMatchNotStarted = matchData.match.status === 'not_started';
    const isMatchCompleted = matchData.match.status === 'completed';

    return (
        <div className="content">
            <Row className="justify-content-center">
                <Col md="12">
                    <Card>
                        <CardHeader className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                            <div className="d-flex align-items-center mb-3 mb-md-0">
                                <Link to="/dashboard/football/data-center?tab=matches" className="btn btn-secondary me-3">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div className="d-flex flex-column">
                                    <CardTitle tag="h2" className="mb-0 text-left">{matchData.match.title}</CardTitle>
                                    <p className="text-muted text-sm">{matchData.match.tournament.name} at {matchData.match.stadium.name}</p>
                                </div>
                            </div>
                            <div className="mt-3 mt-md-0">
                                {/* Slug Row */}
                                <div className="d-flex align-items-center mb-2">
                                    <span className="text-muted small fw-bold" style={{ width: "50px" }}>Slug</span>
                                    <span className="font-monospace bg-light px-2 py-1 rounded" title={matchData.match.m_slug}>
                                        {truncateId(matchData.match.m_slug)}
                                    </span>
                                    <Button
                                        id="copy-slug-button"
                                        color="secondary"
                                        outline
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => handleCopy(matchData.match.m_slug, 'slug')}
                                    >
                                        <FontAwesomeIcon icon={copiedItem === 'slug' ? faCheck : faCopy} />
                                    </Button>
                                    <UncontrolledTooltip placement="top" target="copy-slug-button">
                                        {copiedItem ? 'Copied!' : 'Copy Slug'}
                                    </UncontrolledTooltip>
                                </div>

                                {/* Key Row */}
                                <div className="d-flex align-items-center">
                                    <span className="text-muted small fw-bold" style={{ width: "50px" }}>Key</span>
                                    <span className="font-monospace bg-light px-2 py-1 rounded" title={matchData.match.key}>
                                        {truncateId(matchData.match.key)}
                                    </span>
                                    <Button
                                        id="copy-key-button"
                                        color="secondary"
                                        outline
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => handleCopy(matchData.match.key, 'key')}
                                    >
                                        <FontAwesomeIcon icon={copiedItem === 'key' ? faCheck : faCopy} />
                                    </Button>
                                    <UncontrolledTooltip placement="top" target="copy-key-button">
                                        Copy Key
                                    </UncontrolledTooltip>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody className="p-4">
                            <Row className="text-center align-items-center">
                                <Col md="4" className="d-flex flex-column align-items-center">
                                    <TeamLogo team={homeTeam} />
                                    <CardTitle tag="h3" className="mt-2 text-primary">{homeTeam?.name}</CardTitle>
                                </Col>
                                <Col md="4">
                                    <h1 className="display-4 fw-bold">{matchData.score ? `${matchData.score.home} - ${matchData.score.away}` : 'vs'}</h1>
                                    <h4 className="text-muted">
                                        {isMatchLive && <span className="badge bg-primary text-uppercase p-2 mt-2" style={{ animation: 'pulse 1.5s infinite' }}>Live</span>}
                                        {isMatchNotStarted && <span className="badge bg-secondary text-uppercase p-2 mt-2">Not Started</span>}
                                        {isMatchCompleted && <span className="badge bg-success text-uppercase p-2 mt-2">Completed</span>}
                                    </h4>
                                    {isMatchCompleted && (
                                        <>
                                            {winner ? <h3 className="h5 mt-2 fw-bold text-success">{winner.name} Won</h3> : <h3 className="h5 mt-2 fw-bold text-dark">Match Drawn</h3>}
                                            {manOfTheMatch && manOfTheMatch !== 'N/A' && <p className="fw-bold text-info">Man of the Match: <span className="fst-italic">{manOfTheMatch}</span></p>}
                                        </>
                                    )}
                                    <p className="text-muted mt-1">{formatDate(matchData.match.start.timestamp)} at {formatTime(matchData.match.start.timestamp)}</p>
                                </Col>
                                <Col md="4" className="d-flex flex-column align-items-center">
                                    <TeamLogo team={awayTeam} />
                                    <CardTitle tag="h3" className="mt-2 text-danger">{awayTeam?.name}</CardTitle>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {!isMatchNotStarted && (
                        <Row className="mt-4">
                            <Col md="6">
                                <Card>
                                    <CardHeader><CardTitle tag="h4">Events Timeline</CardTitle></CardHeader>
                                    <CardBody>
                                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {matchData.timeline?.length > 0 ? (
                                                <ul className="list-unstyled">
                                                    {matchData.timeline.map(event => (
                                                        <li key={event.key} className={`d-flex align-items-center mb-3 p-3 rounded ${getEventStyle(event.event)}`}>
                                                            <span className="fw-bold text-dark me-3" style={{ minWidth: '4rem' }}>{event.time.label}</span>
                                                            <div className="me-3"><EventIcon event={event.event} /></div>
                                                            <span className="flex-grow-1 text-dark">{event.comment}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <p className="text-muted">No events reported yet.</p>}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="6">
                                <Card>
                                    <CardHeader><CardTitle tag="h4">Match Statistics</CardTitle></CardHeader>
                                    <CardBody>
                                        <StatBar label="Possession %" homeValue={matchData.stats?.possession?.home} awayValue={matchData.stats?.possession?.away} />
                                        <StatBar label="Shots on Target" homeValue={matchData.stats?.shot_on_target?.home} awayValue={matchData.stats?.shot_on_target?.away} />
                                        <StatBar label="Fouls" homeValue={matchData.stats?.fouls?.home} awayValue={matchData.stats?.fouls?.away} />
                                        <StatBar label="Corners" homeValue={matchData.stats?.corner_kicks?.home} awayValue={matchData.stats?.corner_kicks?.away} />
                                        <StatBar label="Substitutions" homeValue={homeSubs} awayValue={awaySubs} />
                                    </CardBody>
                                </Card>
                                <Card className="mt-4">
                                    <CardHeader><CardTitle tag="h4">Lineups</CardTitle></CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col md="6">
                                                <CardTitle tag="h5" className="mt-2 text-primary">{homeTeam?.name || 'Home Team'}</CardTitle>
                                                {renderPlayerList(homeTeam, matchData, getPositionEmoji)}
                                            </Col>
                                            <Col md="6">
                                                <CardTitle tag="h5" className="mt-2 text-danger">{awayTeam?.name || 'Away Team'}</CardTitle>
                                                {renderPlayerList(awayTeam, matchData, getPositionEmoji)}
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {isMatchNotStarted && (
                        <Card className="mt-4 text-center">
                            <CardBody>
                                <CardTitle tag="h4">Match Has Not Started</CardTitle>
                                <p className="text-muted mt-2">Live updates and statistics will appear here once the match begins.</p>
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export default MatchDetails;